import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import classNames from 'classnames';

import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';

import {
  ChevronDownIcon,
  ColumnsIcon,
  GraphIcon,
  LinkExternalIcon,
  PlayIcon,
} from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import { RELATIVE_TIME_RANGES } from '../../../constants/time.constants';

import changeAssayStatus from '../../../utils/api_functions/changeAssayStatus';
import { getThemeColor } from '../../../utils/theme-utils';
import { getTimeRange } from '../../../utils/date_utils';

import useAssayList from '../useAssayList';
import useAssayResults from '../useAssayResults';
import usePipelineData from '../../pipelines/PipelineDataContext';
import useWorkspaceContext from '../../workspaces/WorkspaceContext/WorkspaceContext';

import AssayDetailModal from '../AssayDetailModal/AssayDetailModal';
import AssayFilterControls from '../AssayFilterControls/AssayFilterControls';
import BlankSlate from '../../common/BlankSlate/BlankSlate';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ModalAssayChart from '../ModalAssayChart/ModalAssayChart';
import TimeRangePicker from '../../TimeRangePicker/TimeRangePicker';

import { InvisibleActionButton } from '../../common/CardView/InvisibleActionButton';

const EmptyListGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={GraphIcon}
    size={64}
  />
)

const CollapsibleItemContent = ({
  label,
  expanded,
  children,
  actionList,
  onActionSelect,
  setCollapsed
}) => {
  useEffect(() => {
    setCollapsed(!expanded);
  }, [expanded, setCollapsed]);

  return (
    <CardView disableFullHeight>
      <div className="flex justify-between items-center">
        <CardHeader>{label}</CardHeader>
        <div className="flex items-center">
          <InvisibleActionButton
            actionListArray={actionList}
            actionListOnSelect={onActionSelect}
          />
          <AccordionItemHeading className="m-1">
            <AccordionItemButton className="p-2 rounded hover:bg-border-light">
              <ChevronDownIcon
                fill={getThemeColor('brand')}
                size={24}
                className={classNames(
                  expanded ? 'chevron-up' : 'chevron-down',
                )}
              />
            </AccordionItemButton>
          </AccordionItemHeading>
        </div>
      </div>
      <AccordionItemPanel>
        { children }
      </AccordionItemPanel>
    </CardView>
  )
}

const CollapsibleItem = ({ children, ...rest }) => (
  <AccordionItem className="w-full my-2 p-1">
    <AccordionItemState>
      {({ expanded }) => (
        <CollapsibleItemContent {...rest} expanded={expanded}>
          {children}
        </CollapsibleItemContent>
      )}
    </AccordionItemState>
  </AccordionItem>
)

const AssayItem = ({
  item,
  refetchAssays,
  handleStatusChange,
  setAssayDetailsToShow,
  initialRange = RELATIVE_TIME_RANGES[7],
}) => {
  const [selectedRun, setSelectedRun] = useState(null);
  const [timeRange, setTimeRange] = useState(initialRange);
  const [collapsed, setCollapsed] = useState(false);

  const assayStatus = item?.active;
  const shouldFetchResults = useCallback(() => !collapsed && assayStatus, [assayStatus, collapsed]);
  const timeRangeArr = useMemo(() => getTimeRange(timeRange), [timeRange]);

  const {
    data: resultsData,
    error: resultsError,
    isLoading: areResultsLoading,
    refetchAssayResults,
  } = useAssayResults({
    assayID: item?.id,
    startTimestamp: timeRangeArr[0],
    endTimestamp: timeRangeArr[1],
    shouldFetchResults,
  })

  const refetch = () => {
    refetchAssays();
    refetchAssayResults();
  }

  const assayActions = [
    {
      id: 'changeAssayStatus',
      label: assayStatus? 'Pause Assay' : 'Resume Assay',
      icon: assayStatus ? ColumnsIcon : PlayIcon,
    },
    {
      id: 'showDetails',
      label: 'Show Assay Details',
      icon: LinkExternalIcon,
    },
  ];

  const onAssayActionClicked = (actionId) => {
    if (actionId === 'showDetails') {
      setAssayDetailsToShow(item);
    }
    if (actionId === 'changeAssayStatus') {
      handleStatusChange(item.id, !assayStatus);
      setTimeout(refetchAssays, 1000)
    }
  }

  useEffect(() => {
    if (!collapsed) {
      setTimeRange(initialRange);
    }
  }, [initialRange, collapsed])

  return (
    <CollapsibleItem
      label={item?.name}
      setCollapsed={setCollapsed}
      actionList={assayActions}
      onActionSelect={onAssayActionClicked}
    >
      <div className="mb-2 mx-2">
        <TimeRangePicker
          defaultRange={initialRange}
          selectedRange={timeRange}
          onChange={setTimeRange}
        />
      </div>
      <ModalAssayChart
        assayName={item?.name}
        assayRunResults={resultsData}
        assayDetails={item}
        selectedRun={selectedRun}
        setSelectedRun={setSelectedRun}
        showDetailsFor={setAssayDetailsToShow}
        refetchAssays={refetch}
        message={resultsError}
        isLoading={areResultsLoading}
        loadingText="Loading..."
        showIconMenu
        hideNormalDropdownOptions
        hideCardTitle
      />
    </CollapsibleItem>
  )
}

const AssayChartList = ({
  items,
  setAssayDetailsToShow,
  refetchAssays,
  initialRange = RELATIVE_TIME_RANGES[7],
}) => (
  <Accordion
    className="w-full max-h-[48rem]"
    allowMultipleExpanded
    allowZeroExpanded
  >
    {
      items?.map(item => (
        <AssayItem
          key={item.id}
          item={item}
          initialRange={initialRange}
          refetchAssays={refetchAssays}
          setAssayDetailsToShow={setAssayDetailsToShow}
          handleStatusChange={changeAssayStatus}
        />
      ))
    }
  </Accordion>
)

const LoadingStatesManager = ({ 
  isListLoading,
  assayData,
  firstAssayRunCompleted,
  assayFilter,
  setAssayDetailsToShow,
  refetchAssays 
}) => {
  if (isListLoading) {
    return (
      <div className="relative h-52">
        <LoadingSpinner
          animationKey="assay-list-loading-spinner"
          loadingText="Loading your assays"
          useOverlay
        />
      </div>
    )
  }

  if (!isListLoading && assayData.length !== 0 && !firstAssayRunCompleted) {
    return (
      <div className="relative h-52">
        <LoadingSpinner
          animationKey="assay-list-loading-spinner"
          loadingText="Creating First Assay..."
          useOverlay
        />
      </div>
    )
  }

  return (
    <div className="overflow-scroll flex-grow">
      <AssayChartList
        initialRange={assayFilter?.timeRange}
        items={assayData}
        setAssayDetailsToShow={setAssayDetailsToShow}
        refetchAssays={refetchAssays}
      />
    </div>
  )
}


export const PipelineAssayChartList = () => {
  const [assayDetailToShow, setAssayDetailsToShow] = useState(null);
  const [assayFilter, setAssayFilter] = useState({
    name: '',
    active: true,
    sortBy: 'CreationDateDesc',
  });
  const [firstAssayRunCompleted, setFirstAssayRunCompleted] = useState(false)

  const { pipeline } = usePipelineData();
  const { currentWorkspaceId } = useWorkspaceContext();

  const {
    data: assayData,
    error,
    isLoading: isListLoading,
    refetchAssays
  } = useAssayList({
    pipelineID: pipeline?.id,
    workspaceID: currentWorkspaceId,
    assayFilter,
  });

  useEffect(() => {
    const firstAssay = assayData[assayData.length-1]
    if (firstAssay?.lastRun) {
      setFirstAssayRunCompleted(true)
    }
  }, [assayData])

  return (
    <>
      <div className="relative flex flex-col">
        {
          isListLoading ? (
            <LoadingSpinner
              animationKey="assay-list-loading-spinner"
              loadingText="Loading..."
              useOverlay
            />
          ) : null
        }
        <AssayFilterControls onFilterControlChanged={setAssayFilter} />
        {
          error ? (
            <ErrorSlate errorText={error} />
          ) : !assayData?.length ? (
            <BlankSlate
              className="p-4"
              mainText="There are currently no assays available for this pipeline"
              subText="Create one to get started!"
              Graphic={EmptyListGraphic}
            />
          ) : (
            <LoadingStatesManager 
              isListLoading={isListLoading}
              assayData={assayData}
              firstAssayRunCompleted={firstAssayRunCompleted}
              assayFilter={assayFilter}
              setAssayDetailsToShow={setAssayDetailsToShow}
              refetchAssays={refetchAssays }
            />
          )
        }
      </div>
      <AssayDetailModal
        isShown={!!assayDetailToShow}
        onModalToggle={() => setAssayDetailsToShow(null)}
        assayDetails={assayDetailToShow}
      />
    </>
  )
};

export default PipelineAssayChartList;
